import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Logo } from "../components/Logo";
import { Lock, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/** Bloco 2 — Item 3: definir senha via link mágico de convite. */
export default function DefinirSenha() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const token = params.get("token") || params.get("reset") || "";

  const [phase, setPhase] = useState("validating"); // validating | invalid | form | submitting | done
  const [user, setUserLocal] = useState(null);
  const [err, setErr] = useState("");
  const [nova, setNova] = useState("");
  const [conf, setConf] = useState("");

  const hasMinLen = nova.length >= 8;
  const hasNumber = /\d/.test(nova);
  const match = nova && nova === conf;
  const podeEnviar = hasMinLen && hasNumber && match && phase === "form";

  useEffect(() => {
    if (!token) { setPhase("invalid"); setErr("Link sem token."); return; }
    (async () => {
      try {
        const { data } = await api.get("/auth/validar-convite", { params: { token } });
        setUserLocal(data);
        setPhase("form");
      } catch (e) {
        setPhase("invalid");
        setErr(e?.response?.data?.detail || "Link inválido ou expirado");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!podeEnviar) return;
    setPhase("submitting");
    try {
      const { data } = await api.post("/auth/definir-senha", {
        token, nova_senha: nova, confirmar_senha: conf,
      });
      // Login automático
      localStorage.setItem("lab_token", data.token);
      localStorage.setItem("lab_user", JSON.stringify(data.user));
      setUser?.(data.user);
      toast.success("Senha definida! Bem-vindo(a).");
      setPhase("done");
      const home = data.user?.role === "cliente" ? "/cliente/home" : "/app/dashboard";
      setTimeout(() => nav(home, { replace: true }), 500);
    } catch (err2) {
      toast.error(err2?.response?.data?.detail || "Falha ao definir senha");
      setPhase("form");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-50 to-zinc-100 p-6">
      <Card className="w-full max-w-md p-8" data-testid="page-definir-senha">
        <div className="flex flex-col items-center mb-6">
          <Logo size="md" />
          <div className="mt-4 p-3 rounded-full bg-orange-100">
            <Lock className="w-6 h-6 text-orange-600" />
          </div>
        </div>

        {phase === "validating" && (
          <div className="text-center py-8 text-zinc-500">
            <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Validando seu convite…
          </div>
        )}

        {phase === "invalid" && (
          <div className="text-center py-6">
            <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <h1 className="text-lg font-semibold">Link inválido ou expirado</h1>
            <p className="text-sm text-zinc-500 mt-2">{err}</p>
            <p className="text-xs text-zinc-400 mt-4">Solicite um novo convite ao administrador.</p>
            <Button onClick={() => nav("/login")} variant="outline" className="mt-4">Voltar ao login</Button>
          </div>
        )}

        {(phase === "form" || phase === "submitting" || phase === "done") && user && (
          <>
            <h1 className="text-xl font-bold text-center mb-1">Defina sua senha</h1>
            <p className="text-sm text-zinc-500 text-center mb-6">
              Olá, <strong>{user.name}</strong>! Crie uma senha pessoal para acessar a plataforma.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email-readonly">E-mail</Label>
                <Input id="email-readonly" value={user.email} readOnly className="bg-zinc-50" />
              </div>
              <div>
                <Label htmlFor="nova">Nova senha</Label>
                <Input id="nova" type="password" value={nova} onChange={(e) => setNova(e.target.value)} required data-testid="def-nova" />
              </div>
              <div>
                <Label htmlFor="conf">Confirmar senha</Label>
                <Input id="conf" type="password" value={conf} onChange={(e) => setConf(e.target.value)} required data-testid="def-conf" />
              </div>
              <ul className="text-xs space-y-1">
                <li className={hasMinLen ? "text-emerald-600" : "text-zinc-400"}>
                  {hasMinLen ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : "•"} Mínimo 8 caracteres
                </li>
                <li className={hasNumber ? "text-emerald-600" : "text-zinc-400"}>
                  {hasNumber ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : "•"} Pelo menos 1 número
                </li>
                <li className={match ? "text-emerald-600" : "text-zinc-400"}>
                  {match ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : "•"} Senhas conferem
                </li>
              </ul>
              <Button
                type="submit"
                disabled={!podeEnviar || phase === "submitting"}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50"
                data-testid="def-submit"
              >
                {phase === "submitting" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                Definir senha e entrar
              </Button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
